"use client";

import { IconX } from "@decibeltrade/react-icons";
import { Button, type ButtonVariants, IconButton } from "@decibeltrade/web-core/components";
import { css, cx } from "styled-system/css";
import { flex } from "styled-system/patterns";

export interface TopBannerCta {
  /** The label text rendered inside the CTA button. */
  label: string;
  /** Called when the CTA button is clicked. */
  onClick: () => void;
  /** The visual style of the button. Defaults to `"brand"`. */
  variant?: Extract<ButtonVariants["variant"], "brand" | "text">;
}

type DismissProps =
  | {
      /** Called when the dismiss (×) button is clicked. */
      onDismiss: () => void;
      /** Accessible label for the dismiss button. */
      dismissLabel: string;
    }
  | { onDismiss?: undefined; dismissLabel?: undefined };

export type TopBannerProps = {
  /** Optional icon rendered to the left of the title. */
  icon?: React.ReactNode;
  /** The primary text of the banner. */
  title: string;
  /** Supporting text rendered beside the title. */
  body?: string;
  /** One or more action buttons rendered to the right of the content area. */
  ctas?: TopBannerCta[];
  /** One row on phones: hides the body below `sm` and the icon below `xs`, with 44px touch targets. */
  compact?: boolean;
} & DismissProps;

/**
 * A full-width banner strip rendered beneath the sticky AppHeader. Scrolls
 * with the page so it doesn't compete with the nav for sticky real estate.
 */
export function TopBanner({
  icon,
  title,
  body,
  ctas,
  onDismiss,
  dismissLabel,
  compact,
}: TopBannerProps) {
  return (
    <div data-top-banner="" data-testid="top-banner" className={bannerStyles}>
      {icon && (
        <span
          className={cx(
            css({ flexShrink: 0, color: "text.tertiary" }),
            compact && compactIconStyles,
          )}
        >
          {icon}
        </span>
      )}
      <div className={contentStyles}>
        <span className={css({ textStyle: "highlight.md", color: "text.primary" })}>{title}</span>
        {body && (
          <span
            className={cx(
              css({ textStyle: "body.md", color: "text.secondary" }),
              compact && compactHiddenStyles,
            )}
          >
            {body}
          </span>
        )}
      </div>
      {ctas && ctas.length > 0 && (
        <div className={ctaGroupStyles}>
          {ctas.map((cta) => (
            <Button
              key={cta.label}
              onClick={cta.onClick}
              variant={cta.variant ?? "brand"}
              size="sm"
              className={cx(compact && touchTargetStyles)}
            >
              {cta.label}
            </Button>
          ))}
        </div>
      )}
      {onDismiss && (
        <IconButton
          onClick={onDismiss}
          ariaLabel={dismissLabel}
          variant="text"
          size="sm"
          className={cx(css({ flexShrink: 0 }), compact && touchTargetStyles)}
        >
          <IconX />
        </IconButton>
      )}
    </div>
  );
}

const bannerStyles = flex({
  w: "full",
  bg: "background.primary",
  borderBottom: "1px solid",
  borderColor: "border.secondary",
  align: "center",
  gap: "12",
  px: "16",
  py: "12",
});

const contentStyles = flex({ flex: "1", align: "center", gap: "8", flexWrap: "wrap", minW: "0" });

const ctaGroupStyles = flex({ align: "center", gap: "8", flexShrink: 0 });

const compactHiddenStyles = css({ hideBelow: "sm" });

const compactIconStyles = css({ hideBelow: "xs" });

const touchTargetStyles = css({
  _after: {
    content: '""',
    position: "absolute",
    insetBlock: "[calc(-6px - {borderWidths.100})]",
    insetInline: "[calc(-2px - {borderWidths.100})]",
  },
});
